import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
  Chip,
  IconButton,
  Button,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link as RouterLink, useParams } from "react-router-dom";

import { useDataStore } from "../dataStore";
import { toMediaUrl, withImageSize } from "../api";
import ImageGalleryModal from "../components/ImageGalleryModal";
import BrandedImageFallback from "../components/BrandedImageFallback";
import PageStateCard from "../components/PageStateCard";
import { brandThemeCssVars } from "../themeTokens";

export default function LocationDetails() {
  const { iref } = useParams();
  const {
    loading,
    error,
    getLocationByIref,
    brandTheme,
    t,
    retryCore,
  } = useDataStore();
  const c = brandTheme;
  const loc = getLocationByIref(iref);

  const images = useMemo(() => {
    const imgs = loc?.locationImage_location || [];

    return imgs.map((x) => ({
      id: x.iref,
      url: toMediaUrl(x.image),
      isMain: x.isMain,
      name: x.name,
    }));
  }, [loc]);

  const mainIndex = useMemo(() => {
    if (!images.length) return 0;

    const idx = images.findIndex((x) => x.isMain);

    return idx >= 0 ? idx : 0;
  }, [images]);

  const mainImg = images[mainIndex]?.url || "";

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const openGalleryAt = (idx) => {
    setGalleryIndex(Math.max(0, Math.min(idx, images.length - 1)));
    setGalleryOpen(true);
  };

  if (loading) {
    return (
      <PageStateCard
        loading
        title={t("common.loading")}
        brandTheme={c}
      />
    );
  }

  if (error) {
    return (
      <PageStateCard
        tone="error"
        title={t("common.loadErrorTitle")}
        description={t("common.loadError")}
        actionLabel={t("common.retry")}
        onAction={retryCore}
        brandTheme={c}
      />
    );
  }

  if (!loc) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: "18px",
          border: "1px solid rgba(124,45,18,0.10)",
          bgcolor: c.surface,
        }}
      >
        <CardContent sx={{ p: 3, textAlign: "center" }}>
          <Typography sx={{ fontWeight: 900, color: c.text }}>
            {t("locations.notFoundTitle")}
          </Typography>

          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            {t("locations.notFoundDesc")}
          </Typography>

          <Button
            component={RouterLink}
            to="/locations"
            variant="contained"
            sx={{
              mt: 2,
              borderRadius: "12px",
              fontWeight: 900,
              textTransform: "none",
            }}
          >
            {t("common.backToLocations")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const city = loc?.address?.city?.name || "";
  const street = loc?.address?.street || "";
  const number = loc?.address?.number || "";
  const addr = `${street} ${number}`.trim();
  const addressLine = [city, addr].filter(Boolean).join(", ");
  const schedule = loc?.locationSchedule_location || [];

  const callHref = loc.phone ? `tel:${loc.phone}` : "";

  const mapsHref = addressLine
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        addressLine
      )}`
    : "";

  const copyAddress = async () => {
    try {
      if (!addressLine) return;

      await navigator.clipboard.writeText(addressLine);
    } catch (e) {
      // Clipboard may be blocked by browser permissions.
    }
  };

  return (
    <Stack
      spacing={{ xs: 2, md: 2.5 }}
      sx={{
        ...brandThemeCssVars(c),
        width: "100%",
        maxWidth: 1180,
        mx: "auto",
      }}
    >
      {/* Back row */}
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <IconButton
          component={RouterLink}
          to="/locations"
          aria-label={t("common.backToLocations")}
          sx={{
            width: 44,
            height: 44,
            bgcolor: c.background,
            border: "1px solid rgba(124,45,18,0.10)",
            color: c.accent,
            "&:hover": {
              bgcolor: c.surface,
            },
          }}
        >
          <ArrowBackRoundedIcon />
        </IconButton>

        <Typography sx={{ fontWeight: 800, color: c.accent }}>
          {t("common.backToLocations")}
        </Typography>
      </Stack>

      {/* Hero */}
      <Card
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: { xs: "18px", md: "24px" },
          border: "1px solid rgba(124,45,18,0.10)",
          boxShadow: "0 24px 64px rgba(33,20,12,0.16)",
          bgcolor: c.surface,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.08fr) minmax(360px, 0.92fr)" },
          }}
        >
          <Box sx={{ position: "relative", minHeight: { xs: 300, sm: 400, md: 440, lg: 560 } }}>
            <Box
              component={images.length ? "button" : "div"}
              type={images.length ? "button" : undefined}
              onClick={() => images.length ? openGalleryAt(mainIndex) : null}
              aria-label={
                images.length
                  ? `${t("common.image")}: ${loc.name}`
                  : undefined
              }
              sx={{
                cursor: images.length ? "pointer" : "default",
                position: { xs: "relative", lg: "absolute" },
                inset: { lg: 0 },
                height: { xs: 300, sm: 400, md: 440, lg: "100%" },
                width: "100%",
                p: 0,
                border: 0,
                textAlign: "inherit",
                bgcolor: c.background,
                "&:focus-visible": {
                  outline: `4px solid ${c.primary}`,
                  outlineOffset: -4,
                },
              }}
            >
              {mainImg ? (
                <img
                  src={withImageSize(mainImg, "l")}
                  alt={loc.name}
                  loading="eager"
                  decoding="async"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              ) : (
                <BrandedImageFallback
                  icon={<StorefrontRoundedIcon />}
                  label={t("common.noImage")}
                  brandTheme={c}
                />
              )}
            </Box>

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(17,17,17,0.02) 34%, rgba(17,17,17,0.50) 100%)",
                pointerEvents: "none",
              }}
            />

            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: "absolute",
                left: 14,
                top: 14,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {city ? (
                <Chip
                  icon={<PlaceOutlinedIcon />}
                  label={city}
                  sx={{
                    fontWeight: 900,
                    color: c.text,
                    bgcolor: c.surface,
                    border: "1px solid rgba(255,255,255,0.36)",
                    "& .MuiChip-icon": { color: c.accent },
                  }}
                />
              ) : null}

              {loc.locationType != null ? (
                <Chip
                  icon={<StorefrontRoundedIcon />}
                  label={t("locations.location")}
                  sx={{
                    fontWeight: 900,
                    color: c.onPrimary,
                    bgcolor: c.primary,
                    border: "1px solid rgba(17,17,17,0.08)",
                    "& .MuiChip-icon": { color: c.onPrimary },
                  }}
                />
              ) : null}
            </Stack>

          </Box>

          <CardContent sx={{ p: { xs: 2.25, sm: 3, lg: 4 } }}>
            <Stack spacing={2} sx={{ height: "100%", justifyContent: "center" }}>
              <Box>
                <Typography
                  component="h1"
                  variant="h4"
                  sx={{
                    fontWeight: 950,
                    letterSpacing: 0,
                    lineHeight: 1.03,
                    color: c.text,
                    fontSize: { xs: 32, sm: 42, md: 48 },
                  }}
                >
                  {loc.name}
                </Typography>

                <Stack
                  direction="row"
                  spacing={0.85}
                  alignItems="flex-start"
                  sx={{ mt: 1.25 }}
                >
                  <PlaceOutlinedIcon
                    fontSize="small"
                    sx={{ color: c.accent, mt: "2px", flexShrink: 0 }}
                  />

                  <Typography
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {addressLine || t("common.unavailableAddress")}
                  </Typography>
                </Stack>
              </Box>

              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {callHref ? (
                  <Button
                    startIcon={<CallOutlinedIcon />}
                    component="a"
                    href={callHref}
                    variant="contained"
                    sx={{
                      flex: { xs: "1 1 100%", sm: "0 0 auto" },
                      minHeight: 44,
                      borderRadius: "12px",
                      fontWeight: 900,
                      textTransform: "none",
                      color: c.onCta,
                      bgcolor: c.cta,
                      "&:hover": {
                        color: c.onCta,
                        bgcolor: c.cta,
                        filter: "brightness(0.94)",
                      },
                    }}
                  >
                    {t("locations.call")}
                  </Button>
                ) : null}

                {mapsHref ? (
                  <Button
                    startIcon={<PlaceOutlinedIcon />}
                    component="a"
                    href={mapsHref}
                    target="_blank"
                    rel="noreferrer"
                    variant="outlined"
                    sx={{
                      flex: { xs: "1 1 100%", sm: "0 0 auto" },
                      minHeight: 44,
                      borderRadius: "12px",
                      fontWeight: 900,
                      textTransform: "none",
                      color: c.text,
                      borderColor: c.accent,
                    }}
                  >
                    {t("locations.directions")}
                  </Button>
                ) : null}

                {addressLine ? (
                  <Button
                    startIcon={<ContentCopyOutlinedIcon />}
                    onClick={copyAddress}
                    variant="text"
                    sx={{
                      flex: { xs: "1 1 100%", sm: "0 0 auto" },
                      minHeight: 44,
                      borderRadius: "12px",
                      fontWeight: 900,
                      textTransform: "none",
                      color: c.accent,
                    }}
                  >
                    {t("locations.copyAddress")}
                  </Button>
                ) : null}
              </Stack>

              {loc.phone ? (
                <Chip
                  icon={<CallOutlinedIcon />}
                  label={loc.phone}
                  sx={{
                    alignSelf: "flex-start",
                    fontWeight: 850,
                    bgcolor: c.background,
                    border: "1px solid rgba(124,45,18,0.10)",
                    "& .MuiChip-icon": { color: c.accent },
                  }}
                />
              ) : null}

              {images.length > 1 ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    pt: 0.5,
                    overflowX: "auto",
                    pb: 0.5,
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                >
                  {images.map((im, idx) => (
                    <Box
                      key={im.id}
                      component="button"
                      type="button"
                      onClick={() => openGalleryAt(idx)}
                      aria-label={`${t("common.image")} ${idx + 1}: ${im.name || loc.name}`}
                      sx={{
                        cursor: "pointer",
                        width: 86,
                        height: 64,
                        borderRadius: "12px",
                        overflow: "hidden",
                        border: "2px solid",
                        borderColor:
                          idx === mainIndex
                            ? c.primary
                            : "rgba(124,45,18,0.10)",
                        bgcolor: c.background,
                        flexShrink: 0,
                        p: 0,
                        transition: "transform 140ms ease, border-color 140ms ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          borderColor: "rgba(234,88,12,0.45)",
                        },
                        "&:focus-visible": {
                          outline: `3px solid ${c.primary}`,
                          outlineOffset: 2,
                        },
                        "@media (prefers-reduced-motion: reduce)": {
                          transition: "none",
                          "&:hover": { transform: "none" },
                        },
                      }}
                    >
                      <img
                        src={withImageSize(im.url, "s")}
                        alt={im.name || loc.name}
                        loading="lazy"
                        decoding="async"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          </CardContent>
        </Box>
      </Card>

      {/* Sections */}
      <Stack spacing={{ xs: 1.5, md: 2 }}>
        {loc.description ? (
          <Card
            component="section"
            aria-labelledby="location-about-title"
            elevation={0}
            sx={{
              borderRadius: "18px",
              border: "1px solid rgba(124,45,18,0.10)",
              overflow: "hidden",
              bgcolor: c.surface,
            }}
          >
            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <InfoOutlinedIcon fontSize="small" sx={{ color: c.accent }} />
                <Typography
                  component="h2"
                  id="location-about-title"
                  sx={{ fontWeight: 900, color: c.text, fontSize: 20 }}
                >
                  {t("locations.about")}
                </Typography>
              </Stack>

              <Typography
                color="text.secondary"
                sx={{ mt: 1.25, lineHeight: 1.75, whiteSpace: "pre-line" }}
              >
                {loc.description}
              </Typography>
            </CardContent>
          </Card>
        ) : null}

        {schedule.length ? (
          <Card
            component="section"
            aria-labelledby="location-hours-title"
            elevation={0}
            sx={{
              borderRadius: "18px",
              border: "1px solid rgba(124,45,18,0.10)",
              overflow: "hidden",
              bgcolor: c.surface,
            }}
          >
            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTimeRoundedIcon
                  fontSize="small"
                  sx={{ color: c.accent }}
                />
                <Typography
                  component="h2"
                  id="location-hours-title"
                  sx={{ fontWeight: 900, color: c.text, fontSize: 20 }}
                >
                  {t("locations.schedule")}
                </Typography>
              </Stack>

              <Stack
                component="dl"
                spacing={0}
                sx={{ mt: 1.25, mb: 0 }}
              >
                {schedule
                  .slice()
                  .sort((a, b) => a.day - b.day)
                  .map((s) => (
                    <Stack
                      key={s.iref}
                      direction="row"
                      spacing={1}
                      justifyContent="space-between"
                      sx={{
                        py: 1,
                        borderBottom: "1px solid rgba(124,45,18,0.08)",
                        "&:last-of-type": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <Typography
                        component="dt"
                        sx={{ fontWeight: 850, color: c.text }}
                      >
                        {t(`days.${s.day}`, `${t("days.day")} ${s.day}`)}
                      </Typography>

                      <Typography
                        component="dd"
                        color="text.secondary"
                        sx={{ m: 0, textAlign: "right" }}
                      >
                        {s.interval_start} – {s.interval_end}
                      </Typography>
                    </Stack>
                  ))}
              </Stack>
            </CardContent>
          </Card>
        ) : null}
      </Stack>

      <Divider sx={{ my: 1, borderColor: "rgba(124,45,18,0.10)" }} />

      <ImageGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        title={loc.name}
        images={images}
        index={galleryIndex}
        setIndex={setGalleryIndex}
        t={t}
      />
    </Stack>
  );
}
